import {
  RULE_INSIGHTS_FACTS_MESSAGE,
  RULE_INSIGHTS_READY_MESSAGE,
  RULE_INSIGHTS_RESIZE_MESSAGE,
  RULE_INSIGHTS_RULE_CHANGED_MESSAGE,
  RULE_INSIGHTS_SET_RUNNING_MESSAGE,
  type RuleInsightsRuleChangedMessage,
} from '@reflex/trigger-pane-types';

/**
 * The hash route that serves just the Rule Insights component (Ben Carlson's
 * OneRiver "dedicated route" pattern). We use a URL *hash* rather than query
 * params so debug/simple-consumer embeds need no special query strings: the
 * hash never reaches the server, so any path resolves to our SPA bundle, and a
 * normal Fabric load (no hash) is never misclassified as the component route.
 *
 * The real partner path does NOT rely on this — OneRiver's iframe wrapper is
 * detected via the framework-injected `__iframeType=page` param. This route is
 * the no-query-param mechanism for our own debug harness / lightweight embeds.
 */
export const RULE_INSIGHTS_HASH_ROUTE = '#rule-insights';

/**
 * The Rule Insights iframe postMessage protocol `type` discriminators. These
 * are defined once in the published `@reflex/trigger-pane-types` package (the
 * partner contract) and re-exported here so in-repo trigger-pane code keeps its
 * existing import path while both sides share a single source of truth.
 */
export {
  RULE_INSIGHTS_FACTS_MESSAGE,
  RULE_INSIGHTS_READY_MESSAGE,
  RULE_INSIGHTS_RESIZE_MESSAGE,
  RULE_INSIGHTS_RULE_CHANGED_MESSAGE,
  RULE_INSIGHTS_SET_RUNNING_MESSAGE,
  type RuleInsightsRuleChangedMessage,
};

/**
 * The ids the standalone iframe needs to self-fetch its own facts over the
 * same-origin worker bridge (see `ruleInsightsBridge.ts`). The `ruleId` rides on
 * the hash path segment; `workspaceId` / `reflexId` ride as hash query params so
 * they survive the round-trip without a server (the hash never hits the server).
 */
export interface RuleInsightsHashContext {
  reflexId?: string;
  workspaceId?: string;
}

const HASH_WORKSPACE_PARAM = 'ws';
const HASH_REFLEX_PARAM = 'rx';

/**
 * Builds the Rule Insights hash route, optionally carrying a rule id as a path
 * segment (e.g. `#rule-insights/rule-123`) plus the `workspaceId` / `reflexId`
 * needed for the standalone iframe's self-fetch bridge as hash query params
 * (e.g. `#rule-insights/rule-123?ws=<workspaceId>&rx=<reflexId>`). All values are
 * encoded so ids with slashes or spaces survive the round-trip. When no context
 * ids are supplied the route stays query-free (backward compatible).
 */
export function buildRuleInsightsHash(
  ruleId?: string,
  context?: RuleInsightsHashContext,
): string {
  const base = ruleId
    ? `${RULE_INSIGHTS_HASH_ROUTE}/${encodeURIComponent(ruleId)}`
    : RULE_INSIGHTS_HASH_ROUTE;
  const params = new URLSearchParams();
  if (context?.workspaceId) {
    params.set(HASH_WORKSPACE_PARAM, context.workspaceId);
  }
  if (context?.reflexId) {
    params.set(HASH_REFLEX_PARAM, context.reflexId);
  }
  const query = params.toString();
  return query ? `${base}?${query}` : base;
}

/**
 * Splits a Rule Insights hash into its path segment (everything before the
 * query) and its parsed query params. Centralizes the `?`-splitting both the
 * rule-id and context parsers rely on.
 */
function splitRuleInsightsHash(hash: string): {
  path: string;
  params: URLSearchParams;
} {
  const queryIndex = hash.indexOf('?');
  if (queryIndex === -1) {
    return { params: new URLSearchParams(), path: hash };
  }
  return {
    params: new URLSearchParams(hash.slice(queryIndex + 1)),
    path: hash.slice(0, queryIndex),
  };
}

/**
 * Whether the given location hash is the Rule Insights component route. Matches
 * the bare route (`#rule-insights`) or a route carrying a rule id segment
 * (`#rule-insights/<id>`), but NOT unrelated hashes like `#rule-insights-foo`.
 */
export function isRuleInsightsRoute(hash: string | undefined): boolean {
  if (!hash) {
    return false;
  }
  const { path } = splitRuleInsightsHash(hash);
  return (
    path === RULE_INSIGHTS_HASH_ROUTE ||
    path.startsWith(`${RULE_INSIGHTS_HASH_ROUTE}/`)
  );
}

/**
 * Extracts the rule id carried on the Rule Insights hash route, if any. Returns
 * `undefined` for the bare route or a non-matching hash. Tolerant of any hash
 * query params (`?ws=&rx=`) appended for the self-fetch bridge.
 */
export function parseRuleInsightsRuleId(
  hash: string | undefined,
): string | undefined {
  if (!hash) {
    return undefined;
  }
  const { path } = splitRuleInsightsHash(hash);
  if (!path.startsWith(`${RULE_INSIGHTS_HASH_ROUTE}/`)) {
    return undefined;
  }
  const encoded = path.slice(`${RULE_INSIGHTS_HASH_ROUTE}/`.length);
  if (!encoded) {
    return undefined;
  }
  try {
    return decodeURIComponent(encoded);
  } catch {
    return encoded;
  }
}

/**
 * Extracts the full self-fetch context (`ruleId`, `workspaceId`, `reflexId`)
 * carried on the Rule Insights hash route. The standalone iframe uses this to
 * request its own facts over the same-origin worker bridge when the embedding
 * host never posts facts into the iframe (e.g. the RTH rules hub).
 */
export function parseRuleInsightsContext(hash: string | undefined): {
  reflexId?: string;
  ruleId?: string;
  workspaceId?: string;
} {
  if (!hash) {
    return {};
  }
  const { params } = splitRuleInsightsHash(hash);
  return {
    reflexId: params.get(HASH_REFLEX_PARAM) ?? undefined,
    ruleId: parseRuleInsightsRuleId(hash),
    workspaceId: params.get(HASH_WORKSPACE_PARAM) ?? undefined,
  };
}
